import { PAGE_TYPES, SOT_FIELDS } from 'ai/analytics/events';
import { PROMPT_SOURCES } from 'ai/constants/superchat';
import { buildLyraAdviceField } from 'ai/utils/lyraAdviceCache';

export function buildPageContextFields(pageContext = {}) {
    const {
        pageType, productId, categoryId, categorySeoName, searchTerm
    } = pageContext;
    const fields = {};

    if (pageType === PAGE_TYPES.PRODUCT && productId) {
        fields.sotV15 = productId;
    } else if (pageType === PAGE_TYPES.CATEGORY && categoryId) {
        fields.sotV08 = categoryId;

        if (categorySeoName) {
            fields.sotV163 = categorySeoName;
        }
    } else if (pageType === PAGE_TYPES.SEARCH && searchTerm) {
        fields.sotV104 = searchTerm;
    }

    return fields;
}

/**
 * Appends the prompt origin to the prompt text for sotV402 (GAI-7300):
 * `"<prompt text>; Source:<static|dynamic>"`.
 *
 * Only the exact value `dynamic` passes through; anything else — including a
 * missing `source` — reports `static`, per the Dev Note.
 *
 * @param {string} promptText raw prompt text
 * @param {string} [source] prompt origin from the backend (`static` | `dynamic`)
 * @returns {string} prompt text with the source suffix
 */
export function withPromptSource(promptText, source) {
    const resolvedSource = source === PROMPT_SOURCES.DYNAMIC ? PROMPT_SOURCES.DYNAMIC : PROMPT_SOURCES.STATIC;

    return `${promptText}; Source:${resolvedSource}`;
}

export function buildPromptFields(promptText, messageId, previousMessageId) {
    const fields = {
        sotV402: promptText
    };

    if (messageId) {
        fields.sotV403 = messageId;
    }

    if (previousMessageId) {
        fields.sotV404 = previousMessageId;
    }

    return fields;
}

export function buildImpressionFields(prompts, delayMs = 1000) {
    const promptsString =
        Array.isArray(prompts) && prompts.length > 0
            ? prompts
                .map(p => p?.content || '')
                .filter(Boolean)
                .join(';')
            : '';

    return {
        sotV312: 'SuperChatView',
        sotV219: String(delayMs),
        sotV402: promptsString
    };
}

export function buildSentimentChipFields({ value, position, type }) {
    if (!value || !position) {
        return {};
    }

    const proOrCon = type === 'pros' ? 'Pro' : 'Con';

    return { sotV451: `${value};${position};${proOrCon}` };
}

export function buildLyraAdviceFields() {
    const lyraAdvice = buildLyraAdviceField();

    if (!lyraAdvice) {
        return {};
    }

    return { sotV03: lyraAdvice };
}

export function buildShadeFinderProductFields({
    sessionId, messageId, productId, productName, brandName
}) {
    const fields = {};

    if (sessionId) {
        fields.sotV450 = sessionId;
    }

    if (messageId) {
        fields.sotV403 = messageId;
    }

    if (productId) {
        fields.sotV15 = productId;
    }

    if (productName) {
        fields.sotV215 = productName;
    }

    if (brandName) {
        fields.sotV07 = brandName;
    }

    return fields;
}

export function buildShadeFinderShadeFields({
    sessionId, messageId, skuId, shadeCode, productId
}) {
    const fields = {};

    if (sessionId) {
        fields.sotV450 = sessionId;
    }

    if (messageId) {
        fields.sotV403 = messageId;
    }

    if (skuId) {
        fields.sotV04 = skuId;
    }

    if (shadeCode) {
        fields.sotV180 = shadeCode;
    }

    if (productId) {
        fields.sotV15 = productId;
    }

    return fields;
}

/**
 * Fields for the batched product-comparison impression event (GAI-5969, Event 1).
 * One event per comparison table lists every seen product with the per-product
 * values joined by `;`. The four lists share the SAME product iteration order, so
 * position N always refers to the same product across sotV04/sotV15/sotV215/sotV07.
 * Ordering within the lists is first-seen (the order cards crossed the visibility
 * threshold), not necessarily left-to-right table order. Any `;` inside a value is
 * replaced with `,` so it can never break the delimiter or shift the alignment.
 *
 * @param {object} params
 * @param {string} params.sessionId beautyChatSessionId (sotV450)
 * @param {string} params.messageId superChatMessageId (sotV403)
 * @param {string} params.referrer superChatReferrer (sotV425)
 * @param {{ skuId: string, productId: string, productName: string, brandName: string }[]} params.products
 * @returns {object} sot* fields
 */
export function buildComparisonImpressionFields({
    sessionId, messageId, referrer, products = []
}) {
    if (!Array.isArray(products) || products.length === 0) {
        return {};
    }

    const join = key => products.map(product => String(product[key] ?? '').replace(/;/g, ',')).join(';');

    const fields = {
        sotV04: join('skuId'),
        sotV15: join('productId'),
        sotV215: join('productName'),
        sotV07: join('brandName')
    };

    if (sessionId) {
        fields.sotV450 = sessionId;
    }

    if (messageId) {
        fields.sotV403 = messageId;
    }

    if (referrer) {
        fields.sotV425 = referrer;
    }

    return fields;
}

export function buildSkuSelectionFields({
    sessionId, productId, skuId, colorIqSkuId, referrer
} = {}) {
    const fields = {};

    if (productId) {
        fields[SOT_FIELDS.PRODUCT_ID] = productId;
    }

    if (skuId) {
        fields[SOT_FIELDS.SKU_ID] = skuId;
    }

    if (colorIqSkuId) {
        fields[SOT_FIELDS.COLOR_IQ_SKU_ID] = colorIqSkuId;
    }

    if (referrer) {
        fields[SOT_FIELDS.REFERRER] = referrer;
    }

    if (sessionId) {
        fields[SOT_FIELDS.SESSION_ID] = sessionId;
    }

    return fields;
}

export function buildProductStrings(skuId, options = {}) {
    const { includeContext = true, basketType = 'standard' } = options;

    if (includeContext) {
        return `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=${basketType}`;
    }

    return `;${skuId};;;;eVar26=${skuId}`;
}
